import { ComponentProps, ReactNode, useId } from 'react';

import { InputLabel } from '@/components/InputLabel';
import {
  type FieldSize,
  Input as InputUI,
  inputVariants,
} from '@/components/ui/input';

import { cn } from '@/lib/utils';

type InputProps = Omit<ComponentProps<'input'>, 'size'> & {
  size?: FieldSize;
  label?: ReactNode;
  hint?: ReactNode;
  isRequired?: boolean;
  icon?: ReactNode;
  endAdornment?: ReactNode;
  isInvalid?: boolean;
  tone?: 'default' | 'destructive';
};

const Input = (props: InputProps) => {
  const {
    className,
    label,
    hint,
    isRequired,
    icon,
    endAdornment,
    id,
    isInvalid,
    tone = 'default',
    size = 'md',
    ...restProps
  } = props;

  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <InputLabel
          htmlFor={inputId}
          label={label}
          hint={hint}
          isRequired={isRequired}
          isInvalid={isInvalid}
        />
      )}
      <div className="relative">
        {icon && (
          <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 [&>svg]:size-4">
            {icon}
          </span>
        )}
        <InputUI
          id={inputId}
          aria-invalid={isInvalid || undefined}
          className={cn(
            'h-10',
            inputVariants({ size }),
            'read-only:not-disabled:hover:bg-card read-only:not-disabled:focus-visible:border-input read-only:not-disabled:cursor-default',
            icon && 'pl-8',
            endAdornment && 'pr-8',
            tone === 'destructive' && 'text-destructive-soft-foreground',
            className,
          )}
          {...restProps}
        />
        {endAdornment && (
          <span className="absolute top-1/2 right-2 flex -translate-y-1/2 items-center">
            {endAdornment}
          </span>
        )}
      </div>
    </div>
  );
};

export { Input };
export type { InputProps };
